import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@bcecloud/portal-ui';
import { AuthCard } from '@/components/AuthCard';
import { sendConfirmationEmail, logout } from '@/lib/auth';

/**
 * Email confirmation page — shown when a user's email is not yet confirmed.
 */
export function ConfirmEmailPage(): React.ReactElement {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  /** Re-sends the confirmation email. */
  async function handleResend(): Promise<void> {
    setLoading(true);
    try {
      await sendConfirmationEmail();
      setSent(true);
    } catch {
      // Quietly tolerated — the resend button remains available.
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthCard
      title="Confirm your email"
      subtitle="Please check your inbox and click the confirmation link to activate your account."
    >
      <div className="space-y-5 text-center">
        {sent ? (
          <p role="status" className="text-portal-small font-bold text-portal-success">
            Confirmation email sent! Check your inbox.
          </p>
        ) : (
          <Button variant="primary" disabled={loading} onClick={() => void handleResend()}>
            {loading ? 'Sending…' : 'Resend confirmation email'}
          </Button>
        )}

        <div className="flex items-center justify-center gap-5 text-portal-tiny">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="cursor-pointer font-bold text-brand-link underline-offset-2 hover:underline"
          >
            I&rsquo;ve confirmed my email
          </button>
          <button
            type="button"
            onClick={logout}
            className="cursor-pointer text-portal-muted underline-offset-2 hover:underline"
          >
            Sign out
          </button>
        </div>
      </div>
    </AuthCard>
  );
}
